import $ from 'jquery';
import timing from 'DesignSystem/motion/timing';
import {cfgManager} from './cfg-manager';

export interface IAnimatedCounter {
  /**
   * Number from where the counter starts
   */
  start: number;
  /**
   * Number of end of counter (default is: parseFloat(this.text()))
   */
  end: number;
  /**
   * Number of digits after the decimal point
   */
  decimals: number;
  /**
   * Duration of animation (in miliseconds) (Defaultis: 500)
   */
  duration: number;
  /**
   * Redefinition of options for the responsive (Mobile first). The object key corresponds to the screen width from which the previously declared options are overridden.
   */
  responsive?: { [key: string]: IAnimatedCounter };

  /**
   * Callback function triggered at each step of the animation
   */
  onComplete(self: any, end: number): void | null,

  /**
   * Callback function triggered at each step of the animation
   */
  onChange(self: any, value: number): void | null
}

function process(cfg: IAnimatedCounter | any): void {

  if (typeof cfg !== 'object') {
    cfg = {};
  }

  const defaults: IAnimatedCounter = {
    start: 0,
    end: parseFloat($.trim(this.text())),
    decimals: 0,
    duration: typeof timing.params.std !== 'undefined' ? timing.params.std : 500,
    onComplete: null,
    onChange: null
  };

// Configuration
  let options: { [key: string]: any } | IAnimatedCounter;

  let initialStatus: string;

// PRIVATE FUNCTIONS
// Initialization of the animation
  const init = () => {

    // Managing configuration options
    options = cfgManager(defaults, cfg);

    if (this.length && !isNaN(options.start) && !isNaN(options.end)) {
      initialStatus = this.html();

      this.startAnimation();
    }
  };

  // PUBLIC FUNCTIONS
  /**
   * @method external:"jQuery.fn".animatedCounter.setStart
   * @description Redefine the counter start option
   *
   * @param {number} start=0 - Number from where the counter starts
   *
   */

  this.setStart = (start: string) => {
    const number = parseFloat($.trim(start));
    if (!isNaN(number)) {
      options.start = number;
    }
  };

  /**
   * @method external:"jQuery.fn".animatedCounter.getStart
   * @description Return the counter start option
   *
   * @return {number} - Meter start option
   *
   */

  this.getStart = (): void => {
    return options.start;
  };

  /**
   * @method external:"jQuery.fn".animatedCounter.setEnd
   * @description Redefine the end option of the counter
   *
   * @param {number} end=0 - Number of end of counter
   *
   */

  this.setEnd = (end: string): void => {
    const number = parseFloat($.trim(end));
    if (!isNaN(number)) {
      options.end = number;
    }
  };

  /**
   * @method external:"jQuery.fn".animatedCounter.getEnd
   * @description Return the end option of the counter
   *
   * @return {number} - Meter end option
   *
   */

  this.getEnd = () => {
    return options.end;
  };

  /**
   * @method external:"jQuery.fn".animatedCounter.startAnimation
   * @description Restart animation
   *
   * @return {object} - this
   */

  this.startAnimation = () => {
    // set the content of the field to zero
    this.text(options.start);

    // startAnimation jQuery function parameters
    const params: JQuery.PlainObject = {
      duration: options.duration,
      easing: 'swing',
      step: (value: number) => {
        if (options.decimals > 0) {
          const rounder = Math.pow(10, options.decimals);
          value = Math.round(value * rounder) / rounder;
        } else {
          value = Math.ceil(value);
        }

        if (typeof options.onChange === 'function') {
          options.onChange(this, value);
        } else {
          this.text(value);
        }
      },
      complete: () => {
        if (typeof options.onComplete === 'function') {
          options.onComplete(this, options.end);
        } else {
          this.text(options.end);
        }
      }
    };

    // animate jQuery
    $({Counter: options.start}).animate({
      Counter: options.end
    }, params);

    return this;
  };

  /**
   * @method external:"jQuery.fn".animatedCounter.destroy
   * @description Destroy Counter
   *
   * @return {object} - this
   */

  this.destroy = () => {
    this.setStart = void 0;
    this.setEnd = void 0;
    this.getStart = void 0;
    this.getEnd = void 0;
    this.startAnimation = void 0;
    options = void 0;

    this.html(initialStatus);
    initialStatus = void 0;

    return this;
  };

// Launching the initialization
  init();

  return this;
}

/**
 * @class external:"jQuery.fn".animatedCounter
 *
 * @param {animatedCounter_options} [cfg] - Animated counter configuration options
 *
 * @return {jQuery}
 *
 */

$.fn.extend({

  animatedCounter: process,

});
